import { MapPin, Calendar, Users, Award, ExternalLink } from 'lucide-react';
import { OFFICIAL_CONTACTS, PROJECT_CREATOR } from '../constants/contacts';

const About = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-primary text-white p-8 mb-8">
        <div className="container mx-auto max-w-7xl">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">
            Chi Siamo
          </h1>
          <p className="text-lg text-white/90 max-w-3xl">
            T-Alento: un progetto di riqualificazione che unisce arte, tradizione e innovazione
          </p>
        </div>
      </div>

      <div className="container mx-auto max-w-7xl px-4 py-8">

        {/* Main Content */}
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Il Progetto */}
          <section className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-text-primary mb-6">
              Il Progetto T-Alento
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-text-secondary leading-relaxed mb-4">
                T-Alento è il nome del programma di riqualificazione di Casal Velino che prevede la nascita di un Museo, 
                un Itinerario etnoantropologico e una galleria d'arte diffusa, finanziato nell'ambito del Programma di 
                Azione e Coesione complementare al PON Cultura Sviluppo (FESR) 2014/2021 – Piano Operativo Cultura e 
                Turismo (FSC) 2014/2021 – Avviso Pubblico relativo alla selezione di interventi finalizzati alla 
                riqualificazione e valorizzazione turistico-culturale dei comuni delle regioni Basilicata, Calabria, 
                Campania, Puglia, Sicilia.
              </p>
              <p className="text-text-secondary leading-relaxed">
                Il progetto nasce dalla volontà di valorizzare il patrimonio culturale e naturalistico del Cilento, 
                creando un ponte tra passato e presente attraverso l'arte contemporanea e le tecnologie digitali.
              </p>
            </div>
          </section>

          {/* Le Componenti */}
          <section className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-text-primary mb-6">
              Le Componenti del Progetto
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <MapPin size={16} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary mb-2">Museo Diffuso</h3>
                    <p className="text-text-secondary text-sm">
                      Le opere d'arte contemporanea di Antonio Ievolella sparse nel borgo di Casal Velino, 
                      creando un museo a cielo aperto.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Users size={16} className="text-secondary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary mb-2">Genius Loci</h3>
                    <p className="text-text-secondary text-sm">
                      Personaggi virtuali che guidano i visitatori attraverso la storia e le tradizioni del territorio.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-tertiary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Calendar size={16} className="text-tertiary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary mb-2">Tour Guidati</h3>
                    <p className="text-text-secondary text-sm">
                      36 percorsi tematici per scoprire storia, arte, natura e sapori del Cilento.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Award size={16} className="text-accent" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary mb-2">Produttori Locali</h3>
                    <p className="text-text-secondary text-sm">
                      Una rete di produttori locali che mantengono vive le tradizioni enogastronomiche del Cilento.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* L'Artista */}
          <section className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-text-primary mb-6">
              Antonio Ievolella
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-text-secondary leading-relaxed mb-4">
                Antonio Ievolella è l'artista contemporaneo le cui opere sono al centro del museo diffuso di T-Alento. 
                Le sue installazioni in acciaio corten dialogano con il paesaggio cilentano, creando un ponte tra 
                tradizione e contemporaneità.
              </p>
              <p className="text-text-secondary leading-relaxed">
                Le opere "Magica", "La Piattera del Malocchio", "Le Ruote a Metà" e "Una Goccia alla Volta" 
                trasformano simboli di protezione popolare in installazioni moderne, capaci di raccontare come 
                la tradizione e la creatività convivano, sfidando il tempo.
              </p>
            </div>
          </section>

          {/* Il Territorio */}
          <section className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-text-primary mb-6">
              Casal Velino nel Cilento
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-text-secondary leading-relaxed mb-4">
                Casal Velino è un borgo medievale situato nel cuore del Parco Nazionale del Cilento, Vallo di Diano 
                e Alburni, riconosciuto dall'UNESCO come Patrimonio dell'Umanità. Il territorio si caratterizza per 
                la sua straordinaria biodiversità e per la ricchezza del patrimonio culturale e archeologico.
              </p>
              <p className="text-text-secondary leading-relaxed">
                Il Cilento è la culla della Dieta Mediterranea, riconosciuta dall'UNESCO come Patrimonio Immateriale 
                dell'Umanità, e vanta una tradizione enogastronomica millenaria che si fonde con le bellezze naturali 
                e storiche del territorio.
              </p>
            </div>
          </section>

          {/* Sviluppatori */}
          <section className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-text-primary mb-6">
              Sviluppo Tecnologico
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-text-secondary leading-relaxed mb-4">
                L'app mobile e la webapp di gestione di T-Alento sono state sviluppate da <strong>{PROJECT_CREATOR.name}</strong>, 
                un team di sviluppatori con sede a Benevento specializzato in soluzioni digitali innovative per il turismo 
                culturale e la valorizzazione del patrimonio territoriale.
              </p>
              <p className="text-text-secondary leading-relaxed mb-4">
                <strong>{PROJECT_CREATOR.name}</strong> ha curato l'intero ecosistema digitale del progetto, realizzando:
              </p>
              <ul className="text-text-secondary leading-relaxed mb-4 list-disc list-inside space-y-2">
                <li><strong>App Mobile T-Alento</strong>: disponibile per iOS e Android, integra realtà aumentata per la 
                fruizione delle opere d'arte, chat con i Genius Loci basata su intelligenza artificiale, mappe GPS 
                interattive per i sentieri e notifiche push per gli eventi.</li>
                <li><strong>Webapp di Gestione</strong>: piattaforma amministrativa per la gestione dei contenuti, 
                eventi, opere d'arte, sentieri, produttori locali e configurazione dei prompt dei Genius Loci.</li>
              </ul>
              <p className="text-text-secondary leading-relaxed">
                Le tecnologie utilizzate includono React Native per l'app mobile, React e TypeScript per la webapp, 
                Firebase per il backend, intelligenza artificiale generativa per i Genius Loci, e servizi di 
                geolocalizzazione avanzati per offrire un'esperienza immersiva e personalizzata ai visitatori.
              </p>
            </div>
          </section>

          {/* Contatti */}
          <section className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-text-primary mb-6">
              Contatti
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-3">Informazioni Generali</h3>
                <div className="space-y-2 text-text-secondary">
                  <p><strong>Ente:</strong> {OFFICIAL_CONTACTS.organization}</p>
                  <p><strong>Indirizzo:</strong> {OFFICIAL_CONTACTS.address}</p>
                  <p><strong>Telefono:</strong> {OFFICIAL_CONTACTS.phone}</p>
                  <p>
                    <strong>Sito:</strong>{' '}
                    <a
                      href={OFFICIAL_CONTACTS.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-secondary transition-colors duration-200"
                    >
                      {OFFICIAL_CONTACTS.websiteLabel}
                    </a>
                  </p>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-3">Realizzazione Digitale</h3>
                <div className="space-y-2">
                  <a 
                    href={OFFICIAL_CONTACTS.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-primary hover:text-secondary transition-colors duration-200"
                  >
                    <ExternalLink size={16} />
                    <span>Sito Ufficiale T-Alento</span>
                  </a>
                  <a 
                    href={PROJECT_CREATOR.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-primary hover:text-secondary transition-colors duration-200"
                  >
                    <ExternalLink size={16} />
                    <span>{PROJECT_CREATOR.name} ({PROJECT_CREATOR.websiteLabel})</span>
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default About;
